import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import { posthog } from '../lib/posthog';
import type { TeamBrandingFields } from '../lib/pdf/branding';

interface TeamMembership {
  membership_id: string;
  team_id: string;
  user_id: string;
  role_id: string;
  is_admin: boolean;
  joined_at: string;
  team_name?: string;
  plan_id?: string;
  /** Trial/promo plan expiry from the team row; null = permanent plan */
  plan_expires_at?: string | null;
  /** White-label PDF branding columns from the team row */
  branding?: TeamBrandingFields | null;
}

interface UserAccount {
  user_id: string;
  role_id: string | null;
  primary_team_id: string | null;
  active_team_id: string | null;
  is_staff: boolean;
  is_superadmin: boolean;
  display_name: string | null;
  created_at: string | null;
}

interface ActiveTeamContext {
  team_id: string;
  team_name: string;
  role_id: string;
  plan_id: string;
  /** Trial/promo plan expiry from the team row; null = permanent plan */
  plan_expires_at: string | null;
  is_admin: boolean;
  /** White-label PDF branding columns from the team row */
  branding: TeamBrandingFields | null;
}

interface UserContextType {
  userAccount: UserAccount | null;
  teamMemberships: TeamMembership[];
  loading: boolean;
  error: string | null;
  /** Platform owner — bypasses ALL permission checks unconditionally */
  isSuperadmin: boolean;
  /** Platform staff — cross-team access, permissions evaluated via staff role/plan */
  isStaff: boolean;
  /** @deprecated Use isStaff instead */
  isSystemAdmin: boolean;
  isTeamAdmin: (teamId: string | null | undefined) => boolean;
  getTeamMemberIds: (teamId: string | null | undefined) => Promise<string[]>;
  refetch: () => Promise<void>;
  /** The user's currently active team context */
  activeTeam: ActiveTeamContext | null;
  /** Switch to a different team context */
  setActiveTeam: (teamId: string) => Promise<boolean>;
  /** The user's role in the active team */
  activeRole: string | null;
  /** Whether the user is an admin of the active team */
  isActiveTeamAdmin: boolean;
  /** Whether the user is on a free plan (not staff) */
  isFreePlan: boolean;
  /** Demo team ID from app_setting, null if not configured */
  demoTeamId: string | null;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [userAccount, setUserAccount] = useState<UserAccount | null>(null);
  const [teamMemberships, setTeamMemberships] = useState<TeamMembership[]>([]);
  const [activeTeam, setActiveTeamState] = useState<ActiveTeamContext | null>(null);
  const [demoTeamId, setDemoTeamId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserData = useCallback(async () => {
    if (!user?.id) {
      setUserAccount(null);
      setTeamMemberships([]);
      setActiveTeamState(null);
      setDemoTeamId(null);
      setLoading(false);
      posthog.reset();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch user account, team memberships, and demo team ID in parallel
      const [accountResult, membershipResult, demoTeamResult] = await Promise.all([
        supabase
          .from('user_account')
          .select('*')
          .eq('user_id', user.id)
          .single(),
        supabase
          .from('team_membership')
          .select(`
            membership_id,
            team_id,
            user_id,
            role_id,
            is_admin,
            joined_at,
            team:team_id (team_name, plan_id, plan_expires_at, is_hidden, deleted_at, branding_firm_name, branding_primary_color, branding_secondary_color, branding_logo_path, branding_pdf_enabled)
          `)
          .eq('user_id', user.id),
        supabase
          .from('app_setting')
          .select('setting_value')
          .eq('setting_key', 'demo_team_id')
          .maybeSingle(),
      ]);

      const { data: accountData, error: accountError } = accountResult;
      if (accountError && accountError.code !== 'PGRST116') {
        throw accountError;
      }

      setUserAccount(accountData || null);

      // Set demo team ID (fail silently if not configured)
      setDemoTeamId(demoTeamResult.data?.setting_value || null);

      const { data: memberships, error: membershipError } = membershipResult;
      if (membershipError) {
        throw membershipError;
      }

      // Transform the data to flatten team_name and plan_id,
      // filtering out hidden and soft-deleted teams from user-facing display
      const transformedMemberships = (memberships || [])
        .filter((m: any) => !m.team?.is_hidden && !m.team?.deleted_at)
        .map((m: any) => ({
          membership_id: m.membership_id,
          team_id: m.team_id,
          user_id: m.user_id,
          role_id: m.role_id,
          is_admin: m.is_admin,
          joined_at: m.joined_at,
          team_name: m.team?.team_name || undefined,
          plan_id: m.team?.plan_id || undefined,
          plan_expires_at: m.team?.plan_expires_at ?? null,
          branding: m.team
            ? {
                branding_firm_name: m.team.branding_firm_name ?? null,
                branding_primary_color: m.team.branding_primary_color ?? null,
                branding_secondary_color: m.team.branding_secondary_color ?? null,
                branding_logo_path: m.team.branding_logo_path ?? null,
                branding_pdf_enabled: m.team.branding_pdf_enabled === true,
              }
            : null,
        }));

      setTeamMemberships(transformedMemberships);

      // Set active team context
      const activeTeamId = accountData?.active_team_id || accountData?.primary_team_id;
      let activeMembership = activeTeamId
        ? transformedMemberships.find((m: TeamMembership) => m.team_id === activeTeamId)
        : null;

      // If no active/primary team is set, default to the highest-tier plan team
      if (!activeMembership && transformedMemberships.length > 0) {
        const planTier: Record<string, number> = {
          institutional: 4,
          advisor: 3,
          basic: 2,
          free: 1,
        };
        activeMembership = [...transformedMemberships].sort(
          (a, b) => (planTier[b.plan_id || 'free'] || 0) - (planTier[a.plan_id || 'free'] || 0)
        )[0];
      }

      if (activeMembership) {
        setActiveTeamState({
          team_id: activeMembership.team_id,
          team_name: activeMembership.team_name || '',
          role_id: activeMembership.role_id,
          plan_id: activeMembership.plan_id || 'free',
          plan_expires_at: activeMembership.plan_expires_at ?? null,
          is_admin: activeMembership.is_admin || false,
          branding: activeMembership.branding ?? null,
        });
      }

      // Identify user in PostHog for analytics
      if (accountData && user) {
        posthog.identify(user.id, {
          email: user.email,
          display_name: accountData.display_name,
          is_staff: accountData.is_staff,
          active_team_id: activeMembership?.team_id,
          active_team_name: activeMembership?.team_name,
          plan_id: activeMembership?.plan_id,
        });
      }
    } catch (err: any) {
      console.error('Error fetching user data:', err);
      setError(err.message || 'Failed to fetch user data');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Check if the current user is a platform superadmin (full bypass)
  const isSuperadmin = userAccount?.is_superadmin === true;

  // Check if the current user is a platform staff member (cross-team, configurable permissions)
  const isStaff = userAccount?.is_staff === true;

  // Legacy: Check if the current user is a system-wide admin (deprecated, use isStaff instead)
  const isSystemAdmin = isStaff;

  // Check if the current user is an admin of a specific team
  const isTeamAdmin = useCallback((teamId: string | null | undefined): boolean => {
    if (!teamId) return false;
    const membership = teamMemberships.find(m => m.team_id === teamId);
    return membership?.is_admin === true;
  }, [teamMemberships]);

  // Get all member IDs for a specific team (for checking if submitter is a team member)
  const getTeamMemberIds = useCallback(async (teamId: string | null | undefined): Promise<string[]> => {
    if (!teamId) return [];

    const { data, error } = await supabase
      .from('team_membership')
      .select('user_id')
      .eq('team_id', teamId);

    if (error) {
      console.error('Error fetching team members:', error);
      return [];
    }

    return (data || []).map(m => m.user_id);
  }, []);

  // Set active team - calls the database function and updates local state
  const setActiveTeam = useCallback(async (teamId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('set_active_team', { new_team_id: teamId });

      if (error) {
        console.error('Error setting active team:', error);
        return false;
      }

      if (!data) {
        // User is not a member of this team
        return false;
      }

      // Update local state
      const membership = teamMemberships.find(m => m.team_id === teamId);
      if (membership) {
        setActiveTeamState({
          team_id: membership.team_id,
          team_name: membership.team_name || '',
          role_id: membership.role_id,
          plan_id: membership.plan_id || 'free',
          plan_expires_at: membership.plan_expires_at ?? null,
          is_admin: membership.is_admin || false,
          branding: membership.branding ?? null,
        });
        setUserAccount(prev => prev ? { ...prev, active_team_id: teamId } : null);
      }

      return true;
    } catch (err) {
      console.error('Error setting active team:', err);
      return false;
    }
  }, [teamMemberships]);

  const isFreePlan = activeTeam?.plan_id === 'free' && !isStaff;

  const value: UserContextType = {
    userAccount,
    teamMemberships,
    loading,
    error,
    isSuperadmin,
    isStaff,
    isSystemAdmin, // deprecated
    isTeamAdmin,
    getTeamMemberIds,
    refetch: fetchUserData,
    activeTeam,
    setActiveTeam,
    activeRole: activeTeam?.role_id || null,
    isActiveTeamAdmin: activeTeam?.is_admin || false,
    isFreePlan,
    demoTeamId,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

// Co-located with UserProvider by convention; splitting into a separate file
// would require updating ~34 import sites outside src/hooks and src/contexts,
// which is out of scope here.
// eslint-disable-next-line react-refresh/only-export-components
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
